import { useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { normalizeFullWidthNumerics } from '../../../utils/numericInputNormalize';
import { getTppApiErrorMessage, tppMutationError } from '../../../utils/tppApiError';
import TppModalGuide from '../../common/TppModalGuide';
import { TPP_MODAL_CONTENT_CLASS, TppBtnSpinner } from './tppUiShared';
import { useTppOnModalFreshOpen } from './useTppOnModalFreshOpen';

export type TppCreateCardSubmitPayload = {
  displayNumber: string;
  physicalId: string;
};

export type TppEditCardSubmitPayload = {
  physicalId: string;
  isActive: boolean;
};

export type TppEditCardSeed = {
  displayNumber: number;
  poolKey: string;
  physicalId: string;
  isActive: boolean;
};

type TppCardMasterModalsProps = {
  closeLabel: string;
  createOpen: boolean;
  onCreateHide: () => void;
  /** 開いたときに表示番号の初期値 */
  createNextDisplayNumber: number;
  createPending: boolean;
  createError: unknown;
  onCreateSubmit: (payload: TppCreateCardSubmitPayload) => void;
  editOpen: boolean;
  onEditHide: () => void;
  editSeed: TppEditCardSeed | null;
  editPending: boolean;
  editError: unknown;
  onEditSubmit: (payload: TppEditCardSubmitPayload) => void;
};

function getApiErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    return (error as { response?: { status?: number } }).response?.status;
  }
  return undefined;
}

/** カード登録・マスタ編集（フォーム状態は内部保持） */
export default function TppCardMasterModals({
  closeLabel,
  createOpen,
  onCreateHide,
  createNextDisplayNumber,
  createPending,
  createError,
  onCreateSubmit,
  editOpen,
  onEditHide,
  editSeed,
  editPending,
  editError,
  onEditSubmit,
}: TppCardMasterModalsProps) {
  const { t } = useTranslation();
  const [displayNumber, setDisplayNumber] = useState('');
  const [physicalId, setPhysicalId] = useState('');
  const [editPhysical, setEditPhysical] = useState('');
  const [editActive, setEditActive] = useState(true);

  useTppOnModalFreshOpen(createOpen, () => {
    setDisplayNumber(String(createNextDisplayNumber));
    setPhysicalId('');
  });

  useTppOnModalFreshOpen(editOpen && !!editSeed, () => {
    if (!editSeed) return;
    setEditPhysical(editSeed.physicalId);
    setEditActive(editSeed.isActive);
  });

  return (
    <>
      <Modal
        show={createOpen}
        onHide={onCreateHide}
        centered
        scrollable
        contentClassName={TPP_MODAL_CONTENT_CLASS}
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            if (!createPending && displayNumber && physicalId.trim()) {
              onCreateSubmit({ displayNumber, physicalId });
            }
          }}
        >
          <Modal.Header closeButton closeLabel={closeLabel}>
            <Modal.Title>{t('tpp.create_card_title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <TppModalGuide
              collapsible
              introKey="tpp.create_card_help_intro"
              stepKeys={[
                'tpp.create_card_help_step1',
                'tpp.create_card_help_step2',
                'tpp.create_card_help_step3',
              ]}
            />
            <Form.Group className="mb-2">
              <Form.Label htmlFor="tpp-create-display-number">
                {t('tpp.import_header_display_number')}
              </Form.Label>
              <Form.Control
                id="tpp-create-display-number"
                type="number"
                value={displayNumber}
                onChange={(e) => setDisplayNumber(normalizeFullWidthNumerics(e.target.value))}
              />
              <Form.Text muted>{t('tpp.create_card_display_hint')}</Form.Text>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label htmlFor="tpp-create-physical-id">{t('tpp.physical_id')}</Form.Label>
              <Form.Control
                id="tpp-create-physical-id"
                value={physicalId}
                onChange={(e) => setPhysicalId(e.target.value)}
                placeholder={t('tpp.create_card_physical_placeholder')}
                autoFocus
              />
              <Form.Text muted>{t('tpp.create_card_physical_hint')}</Form.Text>
            </Form.Group>
            {!!createError && (
              <Alert variant="danger">
                {getApiErrorStatus(createError) === 409
                  ? t('tpp.create_card_duplicate', { displayNumber })
                  : getTppApiErrorMessage(createError, t) || t('tpp.create_card_failed')}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="secondary" onClick={onCreateHide}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={createPending || !displayNumber || !physicalId.trim()}
            >
              <TppBtnSpinner show={createPending} />
              {t('tpp.register_card_confirm')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={editOpen}
        onHide={onEditHide}
        centered
        contentClassName={TPP_MODAL_CONTENT_CLASS}
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            if (!editPending && editPhysical.trim()) {
              onEditSubmit({ physicalId: editPhysical, isActive: editActive });
            }
          }}
        >
          <Modal.Header closeButton closeLabel={closeLabel}>
            <Modal.Title>{t('tpp.edit_card_title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editSeed != null && (
              <>
                <p className="small text-muted mb-3">
                  {t('tpp.edit_card_intro', { displayNumber: editSeed.displayNumber,
                    poolKey: editSeed.poolKey })}
                </p>
                <Form.Group className="mb-2">
                  <Form.Label htmlFor="tpp-edit-display-number">
                    {t('tpp.import_header_display_number')}
                  </Form.Label>
                  <Form.Control
                    id="tpp-edit-display-number"
                    type="number"
                    value={editSeed.displayNumber}
                    readOnly
                    disabled
                  />
                  <Form.Text muted>
                    {t('tpp.edit_card_display_readonly')}
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label htmlFor="tpp-edit-physical-id">{t('tpp.physical_id')}</Form.Label>
                  <Form.Control
                    id="tpp-edit-physical-id"
                    value={editPhysical}
                    onChange={(e) => setEditPhysical(e.target.value)}
                    autoFocus
                  />
                  <Form.Text muted>{t('tpp.create_card_physical_hint')}</Form.Text>
                </Form.Group>
                <Form.Check
                  type="switch"
                  id="tppEditActive"
                  className="mb-2"
                  label={t('tpp.card_active')}
                  checked={editActive}
                  onChange={(e) => setEditActive(e.target.checked)}
                />
                {!!editError && (
                  <Alert variant="danger">{tppMutationError(editError, t)}</Alert>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="secondary" onClick={onEditHide}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={editPending || !editPhysical.trim()}
            >
              <TppBtnSpinner show={editPending} />
              {t('common.save')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
